import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import PolicyPage, { PolicyContactBox } from "./PolicyPage";

export default function Cookies() {
  return (
    <PolicyPage
      activePath="/cookies"
      eyebrow="Privacy"
      title="Cookie policy"
      intro="How cookies and browser storage support the storefront, checkout and your saved selections."
      note={{
        title: "Current use",
        content:
          "The storefront currently uses local storage for cart and wishlist selections. It does not currently include its own advertising cookies or cross-site marketing trackers. A hosted payment provider may use strictly necessary security cookies on its own pages.",
      }}
      sections={[
        {
          id: "cookies-meaning",
          shortTitle: "Cookies & storage",
          title: "What cookies and local storage are",
          content: (
            <>
              <p>
                Cookies are small values a website asks a browser to store and return.
                Local storage is a related browser feature that saves data on the device
                without sending it automatically with every page request.
              </p>
              <p>
                Server logs are separate: the hosting service may record details such as
                IP address, browser, requested page, response status and time for security,
                reliability and diagnosis.
              </p>
            </>
          ),
        },
        {
          id: "cookies-essential",
          shortTitle: "Essential storage",
          title: "Strictly necessary functions",
          content: (
            <>
              <div className="policy-definition-list">
                <div>
                  <strong>cartItems</strong>
                  <p>Local storage containing product identifiers, options and quantities so the basket can persist on the device.</p>
                </div>
                <div>
                  <strong>wishlistItems</strong>
                  <p>Local storage containing saved product identifiers for the wishlist.</p>
                </div>
                <div>
                  <strong>Payment security</strong>
                  <p>The hosted payment provider may set cookies needed for authentication, fraud prevention and checkout continuity.</p>
                </div>
              </div>
              <p>
                Essential storage supports a feature requested by the user or helps keep a
                transaction secure. Disabling it can prevent a basket, wishlist or payment
                step from working as expected.
              </p>
            </>
          ),
        },
        {
          id: "cookies-optional",
          shortTitle: "Optional cookies",
          title: "Analytics, preference & marketing cookies",
          content: (
            <>
              <p>
                No optional analytics, personalisation or advertising cookies are currently
                identified in this storefront code. If optional technologies are added,
                this policy will name their purpose and duration and a consent control will
                be shown before use where required.
              </p>
              <p>
                A link to an external site does not place that site’s cookies by itself, but
                the external service may use cookies after you visit it under its own policy.
              </p>
            </>
          ),
        },
        {
          id: "cookies-duration",
          shortTitle: "Duration",
          title: "How long storage remains",
          content: (
            <>
              <p>
                Cart and wishlist values remain until they are cleared in the interface,
                removed through browser settings or deleted by the browser or device. A
                payment provider determines the duration of cookies on its hosted service.
              </p>
              <p>
                Any future cookie table should be reviewed whenever vendors or features
                change so published names, purposes and durations remain accurate.
              </p>
            </>
          ),
        },
        {
          id: "cookies-controls",
          shortTitle: "Your controls",
          title: "Browser controls & privacy choices",
          content: (
            <>
              <p>
                Browser settings can show, block or delete cookies and site storage. The
                exact steps depend on the browser and device. Clearing storage removes saved
                cart and wishlist selections and may sign you out of third-party services.
              </p>
              <p>
                Global privacy signals are not relevant to the current absence of targeted
                advertising on this storefront. If a legally covered sale, sharing or
                targeted-advertising practice is introduced, supported opt-out signals and
                controls will be described on the
                <Link to="/privacy-choices"> privacy choices page</Link>.
              </p>
              <PolicyContactBox label="Cookie question?" />
            </>
          ),
        },
        {
          id: "cookies-changes",
          shortTitle: "Changes",
          title: "Policy updates & contact",
          content: (
            <p>
              Changes to storage use will be reflected by the date on this page. Questions
              can be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.
            </p>
          ),
        },
      ]}
    />
  );
}
